import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { useAdmin } from '@/hooks/use-admin';
import { NavHeader } from '@/components/layout/nav-header';
import { v4 as uuidv4 } from 'uuid';
import ReactPlayer from 'react-player'; // Changed from react-player/lazy
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from "@/components/ui/textarea";
// Removed EditableGenreInput import - using simple inputs instead
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { VenueTimeFormats } from '@/utils/timezone';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  Gift,
  Play,
  Pause,
  Rewind,
  Volume2,
  Maximize,
  Settings2,
  LogOut,
  User as UserIcon,
  Pencil,
  UploadCloud,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  CalendarDays,
  MapPin,
  Users,
  Edit3,
  Save,
  Eye,
  EyeOff,
  Video,
  Music2,
  ListMusic,
  PlusCircle,
  Trash2,
  ArrowLeft,
  GripVertical,
  Plus,
  Download,
  Share2,
  Copy,
  Check,
  Loader2,
} from 'lucide-react';

interface SimpleUser {
  id: number;
  displayName: string | null;
  instagramHandle?: string | null;
  location?: string | null;
}

interface SimpleTimeSlot {
  id: number;
  startTime: string;
  endTime: string;
}

interface KeyMomentData {
  id?: number; // Optional if it's a new key moment not yet saved
  // bookingId?: number; // This would be on the set, not directly here if fetched via set
  setId?: number; // If fetched from DB, it would have setId
  timestamp: string;
  label: string;
  createdAt?: string;
  updatedAt?: string;
}

interface KeyMomentUI {
  clientId: string;
  timestamp: string;
  label: string;
  timestampError?: string;
  timestampEnd: string;
  timestampEndError?: string;
  endMode: "custom" | "30s" | "60s";
}

function timestampToSeconds(ts: string): number {
  const parts = ts.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

/**
 * Parse and normalize a timestamp string.
 * Accepts: ":45" -> "0:45", "6:45" -> "6:45", "1:06:45" -> "1:06:45", "45" -> "0:45"
 * Returns { normalized, error } where error is null if valid.
 */
function parseAndNormalizeTimestamp(raw: string): { normalized: string; error: string | null } {
  const trimmed = raw.trim();
  if (trimmed === '') return { normalized: '', error: null };

  // Reject anything that isn't digits and colons
  if (/[^0-9:]/.test(trimmed)) {
    return { normalized: trimmed, error: 'Use m:ss (6:45) or h:mm:ss (1:06:45)' };
  }

  // Reject empty segments (e.g. "6::45" or leading/trailing colons like "6:" except ":45")
  if (trimmed.includes('::') || trimmed.endsWith(':')) {
    return { normalized: trimmed, error: 'Use m:ss (6:45) or h:mm:ss (1:06:45)' };
  }

  if (trimmed.includes(':')) {
    const parts = trimmed.split(':');

    if (parts.length === 2) {
      // mm:ss (leading colon like ":45" gives parts ["", "45"])
      const minStr = parts[0];
      const secStr = parts[1];
      if (secStr.length !== 2) return { normalized: trimmed, error: 'Seconds must be 2 digits (e.g. :06)' };
      const minutes = minStr === '' ? 0 : parseInt(minStr, 10);
      const seconds = parseInt(secStr, 10);
      if (isNaN(minutes) || isNaN(seconds)) return { normalized: trimmed, error: 'Use m:ss (6:45) or h:mm:ss (1:06:45)' };
      if (seconds < 0 || seconds > 59) return { normalized: trimmed, error: 'Seconds must be 0-59' };
      if (minutes < 0) return { normalized: trimmed, error: 'Minutes must be non-negative' };
      return { normalized: `${minutes}:${String(seconds).padStart(2, '0')}`, error: null };
    }

    if (parts.length === 3) {
      // hh:mm:ss
      const hourStr = parts[0];
      const minStr = parts[1];
      const secStr = parts[2];
      if (minStr.length !== 2) return { normalized: trimmed, error: 'Minutes must be 2 digits with hours (e.g. 1:06:45)' };
      if (secStr.length !== 2) return { normalized: trimmed, error: 'Seconds must be 2 digits (e.g. 1:06:45)' };
      const hours = parseInt(hourStr, 10);
      const minutes = parseInt(minStr, 10);
      const seconds = parseInt(secStr, 10);
      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return { normalized: trimmed, error: 'Use m:ss (6:45) or h:mm:ss (1:06:45)' };
      if (seconds < 0 || seconds > 59) return { normalized: trimmed, error: 'Seconds must be 0-59' };
      if (minutes < 0 || minutes > 59) return { normalized: trimmed, error: 'Minutes must be 0-59 with hours' };
      if (hours < 0) return { normalized: trimmed, error: 'Hours must be non-negative' };
      return { normalized: `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`, error: null };
    }

    return { normalized: trimmed, error: 'Use m:ss (6:45) or h:mm:ss (1:06:45)' };
  }

  // Plain number: treat as seconds
  const seconds = parseInt(trimmed, 10);
  if (isNaN(seconds) || seconds < 0) return { normalized: trimmed, error: 'Use m:ss (6:45) or h:mm:ss (1:06:45)' };
  return { normalized: `0:${String(seconds).padStart(2, '0')}`, error: null };
}

interface SortableKeyMomentProps {
  km: KeyMomentUI;
  index: number;
  canEdit: boolean;
  isSubmitting: boolean;
  onTimestampChange: (clientId: string, value: string) => void;
  onTimestampBlur: (clientId: string) => void;
  onTimestampEndChange: (clientId: string, value: string) => void;
  onTimestampEndBlur: (clientId: string) => void;
  onEndModeChange: (clientId: string, mode: "custom" | "30s" | "60s") => void;
  onLabelChange: (clientId: string, timestamp: string, value: string) => void;
}

function SortableKeyMoment({
  km,
  index,
  canEdit,
  isSubmitting,
  onTimestampChange,
  onTimestampBlur,
  onTimestampEndChange,
  onTimestampEndBlur,
  onEndModeChange,
  onLabelChange,
}: SortableKeyMomentProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: km.clientId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  const disabled = !canEdit || isSubmitting;

  return (
    <div ref={setNodeRef} style={style} className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-300 touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <h4 className="text-sm font-medium text-gray-300">Timestamp {index + 1}</h4>
      </div>
      <div className="pl-6 space-y-3">
        {/* Row 1: Start + Duration + End */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-shrink-0">
            <label htmlFor={`ts-${km.clientId}`} className="block text-xs text-gray-400 mb-1">Start</label>
            <Input
              disabled={disabled}
              id={`ts-${km.clientId}`}
              type="text"
              value={km.timestamp}
              onChange={(e) => onTimestampChange(km.clientId, e.target.value.replace(/[^0-9:]/g, ''))}
              onBlur={() => onTimestampBlur(km.clientId)}
              placeholder="h:mm:ss"
              className={`bg-[#1a1a1a] border text-white placeholder:text-gray-500 w-28 text-sm tabular-nums rounded-md ${km.timestampError ? 'border-red-500' : 'border-white/10'}`}
              maxLength={10}
            />
            {km.timestampError && (
              <p className="text-xs text-red-400 mt-1">{km.timestampError}</p>
            )}
          </div>

          <div className="flex-shrink-0">
            <label className="block text-xs text-gray-400 mb-1">Duration</label>
            <div className="flex items-center gap-1.5 h-10 bg-[#1a1a1a] border border-white/10 rounded-md px-1">
              {(["30s", "60s", "custom"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  disabled={disabled}
                  onClick={() => onEndModeChange(km.clientId, mode)}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    km.endMode === mode
                      ? "bg-white text-black"
                      : "text-white/40 hover:text-white/70"
                  } disabled:opacity-50`}
                >
                  {mode === "custom" ? "Custom" : mode}
                </button>
              ))}
            </div>
          </div>

          {km.endMode === "custom" && (
            <div className="flex-shrink-0">
              <label htmlFor={`ts-end-${km.clientId}`} className="block text-xs text-gray-400 mb-1">End</label>
              <Input
                disabled={disabled}
                id={`ts-end-${km.clientId}`}
                type="text"
                value={km.timestampEnd}
                onChange={(e) => onTimestampEndChange(km.clientId, e.target.value.replace(/[^0-9:]/g, ''))}
                onBlur={() => onTimestampEndBlur(km.clientId)}
                placeholder="h:mm:ss"
                className={`bg-[#1a1a1a] border text-white placeholder:text-gray-500 w-28 text-sm tabular-nums rounded-md ${km.timestampEndError ? 'border-red-500' : 'border-white/10'}`}
                maxLength={10}
              />
            </div>
          )}
        </div>
        {km.timestampEndError && (
          <p className="text-xs text-red-400">{km.timestampEndError}</p>
        )}

        {/* Row 2: Track */}
        <div>
          <label htmlFor={`track-${km.clientId}`} className="block text-xs text-gray-400 mb-1">Track</label>
          <Input
            disabled={disabled}
            id={`track-${km.clientId}`}
            type="text"
            value={km.label}
            onChange={(e) => onLabelChange(km.clientId, km.timestamp, e.target.value)}
            placeholder="Type here..."
            className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 w-full"
          />
        </div>
      </div>
    </div>
  );
}

interface SetData {
  videoUrl?: string | null;
  setlist?: string | null;
  genres?: string[] | null;
  keyMoments?: KeyMomentData[] | null;
  causeLink?: string | null;
  causeLabel?: string | null;
  causeInstagramUrl?: string | null;
  publicVideoUrl?: string | null;
}

interface BookingData {
  id: number;
  djId: number | null;
  timeSlotId: number | null;
  causeLink?: string | null; // Fallback from bookings table
  status: string;
  // video_url, setlist, genres, keyMoments are now part of the 'set' object
  notes: string | null;
  set?: SetData | null; // Added for the nested set data
  createdAt: string | null;
  updatedAt: string | null;
  dj: SimpleUser | null;
  timeSlot: SimpleTimeSlot | null;
  lifecycleStatus: string
}

interface VideoReviewPageProps {
  params?: { bookingId?: string };
}

const VideoReviewPage: React.FC<VideoReviewPageProps> = (props) => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { isAdmin } = useAdmin();
  const { toast } = useToast();
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const playerRef = useRef<any>(null);
  const [isVideoStarted, setIsVideoStarted] = useState(false);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  // Share modal state (admin only)
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [enableVideoDownload, setEnableVideoDownload] = useState(false);
  const [enableAudioDownload, setEnableAudioDownload] = useState(false);
  const [enableTimestampDownloads, setEnableTimestampDownloads] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [shareLinkLoaded, setShareLinkLoaded] = useState(false);
  const savedPermissions = useRef({ video: false, audio: false, timestamps: false });

  const canEdit = useMemo(() => {
    if (isAuthLoading || !bookingData) return false;
    if (isAdmin) return true;
    return user?.id !== undefined && bookingData?.djId !== null && Number(user.id) === bookingData.djId;
  }, [user, isAdmin, bookingData, isAuthLoading]);
  const bookingId = props.params?.bookingId;

  // State for Genres and Key Moments
  const [genres, setGenres] = useState<string[]>(['', '', '']);
  const INITIAL_VISIBLE = 5;
  const MAX_TIMESTAMPS = 20;
  const ADD_INCREMENT = 4;
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const initialKeyMoments: KeyMomentUI[] = Array.from({ length: MAX_TIMESTAMPS }, () => ({
    clientId: uuidv4(),
    timestamp: "",
    label: "",
    timestampEnd: "",
    endMode: "60s" as const,
  }));
  const [keyMomentsUi, setKeyMomentsUi] = useState<KeyMomentUI[]>(initialKeyMoments);
  const [causeLink, setCauseLink] = useState('');
  const [causeLabel, setCauseLabel] = useState('');
  const [causeInstagramUrl, setCauseInstagramUrl] = useState('');
  const [causeLinkType, setCauseLinkType] = useState<"gofundme" | "custom">("custom");
  const [selectedGofundmeId, setSelectedGofundmeId] = useState<number | null>(null);
  const [gofundmeLinks, setGofundmeLinks] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error' | null; text: string | null }>({ type: null, text: null });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [displayedSetlist, setDisplayedSetlist] = useState("");
  
  // Personal info state
  const [artistName, setArtistName] = useState("");
  const [instagramHandle, setInstagramHandle] = useState("");
  const [cityOfOrigin, setCityOfOrigin] = useState("");

  useEffect(() => {
    if (bookingId) {
      const fetchBookingData = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const response = await fetch(`/api/bookings/${bookingId}`);
          if (!response.ok) {
            throw new Error(`Failed to fetch booking data: ${response.statusText}`);
          }
          const data = await response.json();
          setBookingData(data);
        } catch (err: any) {
          setError(err.message || 'An unknown error occurred');
          setBookingData(null);
        }
        setIsLoading(false);
      };
      fetchBookingData();
    } else {
      setIsLoading(false);
      setBookingData(null);
      setError("No booking ID provided."); // Optionally set an error if bookingId is expected
    }
  }, [bookingId]);

  // Fetch GoFundMe links
  useEffect(() => {
    const fetchGofundmeLinks = async () => {
      try {
        const response = await fetch('/api/admin/gofundme');
        if (response.ok) {
          const data = await response.json();
          setGofundmeLinks(data);
        }
      } catch (error) {
        console.error('Error fetching GoFundMe links:', error);
      }
    };
    fetchGofundmeLinks();
  }, []);

  // Load existing share link for this booking
  useEffect(() => {
    if (!bookingId || shareLinkLoaded) return;
    const loadShareLink = async () => {
      try {
        const res = await fetch(`/api/admin/share-links?bookingId=${bookingId}`);
        if (res.ok) {
          const links = await res.json();
          if (links.length > 0) {
            const link = links[0];
            setShareToken(link.token);
            setEnableVideoDownload(link.enableVideoDownload);
            setEnableAudioDownload(link.enableAudioDownload);
            setEnableTimestampDownloads(link.enableTimestampDownloads);
            savedPermissions.current = {
              video: link.enableVideoDownload,
              audio: link.enableAudioDownload,
              timestamps: link.enableTimestampDownloads,
            };
          }
        }
      } catch {
        // Ignore
      }
      setShareLinkLoaded(true);
    };
    loadShareLink();
  }, [bookingId, shareLinkLoaded]);

  // Share link mutation
  const shareLinkMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/share-links", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bookingId: Number(bookingId),
          enableVideoDownload,
          enableAudioDownload,
          enableTimestampDownloads,
        }),
      });
      if (!res.ok) throw new Error("Failed to create share link");
      return res.json();
    },
    onSuccess: (data) => {
      setShareToken(data.token);
      savedPermissions.current = {
        video: enableVideoDownload,
        audio: enableAudioDownload,
        timestamps: enableTimestampDownloads,
      };
      const url = `${window.location.origin}/set/${data.token}`;
      navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    },
  });

  const shareUrl = shareToken ? `${window.location.origin}/set/${shareToken}` : null;
  const permissionsChanged = shareToken && (
    enableVideoDownload !== savedPermissions.current.video ||
    enableAudioDownload !== savedPermissions.current.audio ||
    enableTimestampDownloads !== savedPermissions.current.timestamps
  );

  useEffect(() => {
    if (bookingData) {
      // Initialize personal info
      setArtistName(bookingData.dj?.displayName || '');
      setInstagramHandle(bookingData.dj?.instagramHandle?.replace('@', '') || '');
      setCityOfOrigin(bookingData.dj?.location || '');
      
      if (bookingData.set) { // Check for bookingData.set
        setGenres(bookingData.set.genres || ['', '', '']);
        setDisplayedSetlist(bookingData.set.setlist || "");

        const serverKeyMoments = bookingData.set?.keyMoments || [];
        setKeyMomentsUi(prevKeyMomentsUi =>
          prevKeyMomentsUi.map((uiMoment, index) => {
            const serverMoment = serverKeyMoments[index];
            if (serverMoment) {
              return {
                ...uiMoment,
                timestamp: serverMoment.timestamp,
                label: serverMoment.label,
                timestampEnd: serverMoment.endTime || "",
                endMode: (serverMoment.durationType || "60s") as "30s" | "60s" | "custom",
              };
            }
            return {
              ...uiMoment,
              timestamp: "",
              label: "",
              timestampEnd: "",
              endMode: "60s" as const,
            };
          })
        );
        // Auto-expand visible rows to show existing data
        const filledCount = serverKeyMoments.length;
        if (filledCount > INITIAL_VISIBLE) {
          setVisibleCount(Math.min(filledCount + 2, MAX_TIMESTAMPS));
        }
        // Initialize cause link and label
        const initialLink = bookingData.set?.causeLink ?? bookingData.causeLink ?? '';
        const initialLabel = bookingData.set?.causeLabel ?? '';
        const initialInstagramUrl = bookingData.set?.causeInstagramUrl ?? '';
        setCauseLink(initialLink);
        setCauseLabel(initialLabel);
        setCauseInstagramUrl(initialInstagramUrl);
        
        // Check if it's a preset GoFundMe link
        if (gofundmeLinks.length > 0 && initialLink) {
          const matchingGofundme = gofundmeLinks.find((link: any) => link.link === initialLink);
          if (matchingGofundme) {
            setCauseLinkType("gofundme");
            setSelectedGofundmeId(matchingGofundme.id);
          } else {
            setCauseLinkType("custom");
          }
        }
      }
    } else {
      // If bookingData or bookingData.set is null
      // Reset derived states, preserving clientIds for keyMomentsUi
      setGenres(['', '', '']);
      setDisplayedSetlist("");
      setKeyMomentsUi(prevKeyMomentsUi =>
        prevKeyMomentsUi.map(uiMoment => ({
          ...uiMoment,
          timestamp: "",
          label: "",
          timestampEnd: "",
          endMode: "60s" as const,
        }))
      );
      setVisibleCount(INITIAL_VISIBLE);
      // Reset cause link and label if bookingData is null
      setCauseLink('');
      setCauseLabel('');
      setCauseInstagramUrl('');
      // Reset personal info
      setArtistName('');
      setInstagramHandle('');
      setCityOfOrigin('');
    }
  }, [bookingData, gofundmeLinks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setKeyMomentsUi((items) => {
        const oldIndex = items.findIndex((i) => i.clientId === active.id);
        const newIndex = items.findIndex((i) => i.clientId === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  const handleTimestampChange = useCallback((clientId: string, value: string) => {
    setKeyMomentsUi(prev => prev.map(m =>
      m.clientId === clientId ? { ...m, timestamp: value, timestampError: undefined } : m
    ));
  }, []);

  const handleTimestampBlur = useCallback((clientId: string) => {
    setKeyMomentsUi(prev => prev.map(m => {
      if (m.clientId !== clientId) return m;
      if (!m.timestamp.trim()) return m;
      const { normalized, error } = parseAndNormalizeTimestamp(m.timestamp);
      return { ...m, timestamp: normalized, timestampError: error || undefined };
    }));
  }, []);

  const handleTimestampEndChange = useCallback((clientId: string, value: string) => {
    setKeyMomentsUi(prev => prev.map(m =>
      m.clientId === clientId ? { ...m, timestampEnd: value, timestampEndError: undefined } : m
    ));
  }, []);

  const handleTimestampEndBlur = useCallback((clientId: string) => {
    setKeyMomentsUi(prev => prev.map(m => {
      if (m.clientId !== clientId || m.endMode !== "custom") return m;
      if (!m.timestampEnd.trim()) return m;
      const { normalized, error } = parseAndNormalizeTimestamp(m.timestampEnd);
      if (error) return { ...m, timestampEnd: normalized, timestampEndError: error };
      // Validate end time is after start time and within 60s
      if (normalized && m.timestamp.trim()) {
        const startSec = timestampToSeconds(m.timestamp);
        const endSec = timestampToSeconds(normalized);
        if (endSec <= startSec) {
          return { ...m, timestampEnd: normalized, timestampEndError: 'End time must be after start time' };
        }
        if (endSec - startSec > 60) {
          return { ...m, timestampEnd: normalized, timestampEndError: 'Clip cannot exceed 60 seconds' };
        }
      }
      return { ...m, timestampEnd: normalized, timestampEndError: undefined };
    }));
  }, []);

  const handleEndModeChange = useCallback((clientId: string, mode: "custom" | "30s" | "60s") => {
    setKeyMomentsUi(prev => prev.map(m =>
      m.clientId === clientId ? { ...m, endMode: mode, timestampEnd: mode === "custom" ? m.timestampEnd : "", timestampEndError: undefined } : m
    ));
  }, []);

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const videoPlaceholderUrl = "https://via.placeholder.com/1280x720.png/000000/FFFFFF?text=Video+Not+Available";

  const handleGenreChange = (index: number, value: string) => {
    const newGenres = [...genres];
    newGenres[index] = value;
    setGenres(newGenres);
  };

  const handleKeyMomentChange = (clientId: string, newTimestamp: string, newLabel: string) => {
    setKeyMomentsUi(
      keyMomentsUi.map((km) =>
        km.clientId === clientId ? { ...km, timestamp: newTimestamp, label: newLabel } : km
      )
    );
  };

  const handleMarkAsReady = async () => {
    if (!bookingId) {
      setSubmitMessage({ type: 'error', text: 'Booking ID is missing.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage({ type: null, text: null });

    const payload = {
      genres: genres.filter(g => g.trim() !== ''),
      keyMoments: keyMomentsUi
        .filter(km => km.label.trim() !== '')
        .map(km => ({
          timestamp: km.timestamp,
          endTime: km.endMode === 'custom' ? km.timestampEnd : undefined,
          durationType: km.endMode,
          label: km.label,
        })),
      causeLink: causeLink, // Add causeLink to payload
      causeLabel: causeLabel, // Add causeLabel to payload
      causeInstagramUrl: causeInstagramUrl, // Add causeInstagramUrl to payload
      setlist: displayedSetlist, // Add setlist to payload
      // Personal info
      artistName: artistName,
      instagramHandle: instagramHandle,
      cityOfOrigin: cityOfOrigin,
    };

    try {
      const response = await fetch(`/api/bookings/${bookingId}/mark-as-ready`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          // Include authorization token if needed, e.g., from localStorage or context
          // 'Authorization': `Bearer ${yourAuthToken}`,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (response.ok) {
        setSubmitMessage({ type: 'success', text: 'This set has been submitted and will be reviewed by the Clubhouse.FM team!' });
        // Optionally, update local booking data state or redirect
        // Example: refetchBookingData(); or navigate('/admin/bookings');
        if (bookingData) {
          setBookingData({
            ...bookingData,
            status: 'ready',
            set: {
              ...(bookingData.set || {}),
              genres: payload.genres,
              keyMoments: payload.keyMoments as any, // Assuming KeyMomentData[] type matches
              causeLink: payload.causeLink, // Update local state
              causeLabel: payload.causeLabel, // Update local state
              causeInstagramUrl: payload.causeInstagramUrl, // Update local state
              setlist: payload.setlist, // Update local state
            },
          });
        }
      } else {
        setSubmitMessage({ type: 'error', text: responseData.error || 'Failed to mark booking as ready. Please try again.' });
      }
    } catch (error) {
      console.error('Error marking booking as ready:', error);
      setSubmitMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
    }

    setIsSubmitting(false);
  };

  if (isLoading) {
    return <div className="p-6 text-center text-white bg-clubhouse-black min-h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-400 bg-clubhouse-black min-h-screen">Error: {error}</div>;
  }

  if (!bookingData) {
    return <div className="p-6 text-center text-white bg-clubhouse-black min-h-screen">No booking data found.</div>;
  }

  const displayArtistName = bookingData.dj?.displayName || 'Unknown Artist';
  const videoDate = bookingData.timeSlot?.startTime ? VenueTimeFormats.dateOnly(bookingData.timeSlot.startTime) : 'Unknown Date';
  const awsVideoUrl = bookingData.set?.videoUrl || undefined;
  const publicVideoUrl = bookingData.set?.publicVideoUrl;

  const getYouTubeEmbedUrl = (url: string): string | null => {
    if (!url) return null;
    let videoId: string | null = null;

    // Regex to extract YouTube video ID from various URL formats
    // Supports: 
    // - youtube.com/watch?v=VIDEO_ID
    // - youtu.be/VIDEO_ID
    // - youtube.com/embed/VIDEO_ID
    // - youtube.com/v/VIDEO_ID
    const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube(?:-nocookie)?\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    const match = url.match(youtubeRegex);

    if (match && match[1]) {
      videoId = match[1];
    } else if (url.includes('youtube.com/embed/')) {
        // If it's already an embed link but regex didn't catch it (e.g. no protocol)
        try {
            const pathSegments = new URL(url.startsWith('http') ? url : `https://${url}`).pathname.split('/');
            const embedIndex = pathSegments.indexOf('embed');
            if (embedIndex !== -1 && pathSegments.length > embedIndex + 1) {
                const idCandidate = pathSegments[embedIndex + 1];
                 if (/^[a-zA-Z0-9_-]{11}$/.test(idCandidate)) {
                    videoId = idCandidate;
                 }
            }
        } catch (e) {
            console.warn("Could not parse existing embed URL for video ID:", url, e);
        }
    }

    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null;
  };

  const youtubeEmbedUrl = publicVideoUrl ? getYouTubeEmbedUrl(publicVideoUrl) : null;

  // console.log('[VideoReviewPage] Rendering. AWS URL:', awsVideoUrl, 'Public URL:', publicVideoUrl, 'YouTube Embed URL:', youtubeEmbedUrl);

  return (
    <div className="min-h-screen bg-clubhouse-black text-white flex flex-col">
      <NavHeader />

      <main className="flex-grow animate-page-in">
        <div className="max-w-6xl mx-auto px-4 pt-6 pb-6">
          {/* Back + title row */}
          <div className="flex items-center gap-4 mb-1">
            <button
              onClick={() => window.history.back()}
              className="h-8 w-8 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h2 className="text-[28px] font-semibold tracking-tight flex-1 truncate">{displayArtistName}</h2>
            {isAdmin && (
              <Button
                onClick={() => setShowShareModal(true)}
                variant="outline"
                className="border-white/[0.12] text-white hover:bg-white/[0.06] font-medium px-5 rounded-full shrink-0"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            )}
          </div>
          <p className="text-[14px] text-white/30 ml-12 mt-1">{videoDate}</p>

          {bookingData?.lifecycleStatus === 'ready_to_post' && (
            <div className="mt-6 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 text-[14px] text-center">
              This set has been submitted and will be reviewed by the Clubhouse FM team.
            </div>
          )}
        </div>

        <section className="max-w-6xl mx-auto w-full pb-8 px-4">
          <div className="relative aspect-video rounded-lg overflow-hidden shadow-2xl bg-black">
            {youtubeEmbedUrl ? (
              <div className="absolute top-0 left-0 w-full h-full">
                <iframe
                  src={youtubeEmbedUrl}
                  title="Video Player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            ) : awsVideoUrl ? (
              <div className="absolute top-0 left-0 w-full h-full">
                <ReactPlayer
                  ref={playerRef}
                  url={awsVideoUrl}
                  controls={true}
                  width="100%"
                  height="100%"
                  playing={false}
                  onPlay={() => setIsVideoStarted(true)}
                  onDuration={(d) => setVideoDuration(d)}
                  onProgress={({ playedSeconds }) => setVideoCurrentTime(playedSeconds)}
                  onError={(e, data, hlsInstance?, hlsGlobal?) => {
                    console.error('ReactPlayer Error:', e);
                    if (data) console.error('ReactPlayer Error Data:', data);
                    if (hlsInstance) console.error('ReactPlayer HLS Instance:', hlsInstance);
                    // Try using native video element as fallback
                    const videoContainer = document.querySelector('.react-player');
                    if (videoContainer && awsVideoUrl) {
                      videoContainer.innerHTML = `
                        <video controls style="width: 100%; height: 100%;">
                          <source src="${awsVideoUrl}" type="video/quicktime">
                          <source src="${awsVideoUrl}" type="video/mp4">
                          Your browser does not support the video tag.
                        </video>
                      `;
                    }
                  }}
                  config={{
                    file: {
                      attributes: {
                        controlsList: 'nodownload',
                        onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
                        crossOrigin: 'anonymous',
                      },
                      forceVideo: true,
                    },
                  }}
                />
              </div>
            ) : (
              <img src={videoPlaceholderUrl} alt="Video placeholder" className="w-full h-full object-cover" />
            )}
            {/* Always-visible timestamp overlay */}
            {isVideoStarted && awsVideoUrl && !youtubeEmbedUrl && (
              <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 pointer-events-none z-10">
                <span className="text-[20px] text-white tabular-nums font-medium">
                  {formatTime(videoCurrentTime)} <span className="text-white/40">/ {formatTime(videoDuration)}</span>
                </span>
              </div>
            )}
          </div>
        </section>

        {/* Download links — admin only */}
        {isAdmin && (
          <section className="max-w-6xl mx-auto w-full pb-6 px-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  // TODO: Wire up to actual video download URL
                }}
                className="flex items-center gap-1.5 text-[13px] text-white/30 hover:text-white/60 transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
                Download video
              </button>
              <span className="text-white/10">|</span>
              <button
                onClick={() => {
                  // TODO: Wire up to actual audio download/extraction
                }}
                className="flex items-center gap-1.5 text-[13px] text-white/30 hover:text-white/60 transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
                Download audio
              </button>
            </div>
          </section>
        )}

        <section className="w-full max-w-6xl mx-auto pb-8 px-4">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-semibold">Review set details below</h3>
            <p className="text-sm text-gray-400 mt-2">The Clubhouse.FM team will review this information before posting the set to social media.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Personal Info Section */}
              <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-4">Personal Info</h3>
                
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label htmlFor="artist-name" className="block text-sm font-medium text-gray-300">Artist Name</label>
                    <Input
                      id="artist-name"
                      type="text"
                      value={artistName}
                      onChange={(e) => setArtistName(e.target.value)}
                      placeholder="Enter artist name..."
                      disabled={!canEdit || isSubmitting}
                      className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 w-full"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label htmlFor="instagram-handle" className="block text-sm font-medium text-gray-300">Instagram Handle</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">@</span>
                      <Input
                        id="instagram-handle"
                        type="text"
                        value={instagramHandle}
                        onChange={(e) => setInstagramHandle(e.target.value.replace('@', ''))}
                        placeholder="yourusername"
                        disabled={!canEdit || isSubmitting}
                        className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 w-full pl-8"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <label htmlFor="city-origin" className="block text-sm font-medium text-gray-300">City of Origin</label>
                    <Input
                      id="city-origin"
                      type="text"
                      value={cityOfOrigin}
                      onChange={(e) => setCityOfOrigin(e.target.value)}
                      placeholder="Search by city..."
                      disabled={!canEdit || isSubmitting}
                      className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Social Clips Section */}
              <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-2">Social Clips in Carousel Order</h3>
                {!awsVideoUrl ? (
                  <p className="text-sm text-gray-400">You can only add timestamps once the video has been uploaded. We will send you an email when it's ready (within 24hrs).</p>
                ) : (
                <>
                <p className="text-sm text-gray-400 mb-6">Mark up to 20 moments from the set that you'd like featured in our carousel post. They will be published in the order shown below. You can drag and drop to rearrange them. Max length for custom durations is 60s.</p>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={keyMomentsUi.slice(0, visibleCount).map(km => km.clientId)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-4">
                      {keyMomentsUi.slice(0, visibleCount).map((km, index) => (
                        <SortableKeyMoment
                          key={km.clientId}
                          km={km}
                          index={index}
                          canEdit={canEdit}
                          isSubmitting={isSubmitting}
                          onTimestampChange={handleTimestampChange}
                          onTimestampBlur={handleTimestampBlur}
                          onTimestampEndChange={handleTimestampEndChange}
                          onTimestampEndBlur={handleTimestampEndBlur}
                          onEndModeChange={handleEndModeChange}
                          onLabelChange={handleKeyMomentChange}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                {visibleCount < MAX_TIMESTAMPS && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((prev) => Math.min(prev + ADD_INCREMENT, MAX_TIMESTAMPS))}
                      className="flex items-center gap-2 text-[14px] text-white/40 hover:text-white/70 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add more timestamps
                    </button>
                  </div>
                )}
                </>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Set Genres Section */}
              <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-2">Set Genres</h3>
                <p className="text-sm text-gray-400 mb-4">What are the 3 genres you want us to label your mix?</p>
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label htmlFor="genre-1" className="block text-sm font-medium text-gray-300">
                      Genre 1
                    </label>
                    <Input
                      id="genre-1"
                      type="text"
                      value={genres[0]}
                      onChange={(e) => handleGenreChange(0, e.target.value)}
                      placeholder="Write your genre..."
                      disabled={!canEdit || isSubmitting}
                      className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 w-full"
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="genre-2" className="block text-sm font-medium text-gray-300">
                      Genre 2
                    </label>
                    <Input
                      id="genre-2"
                      type="text"
                      value={genres[1]}
                      onChange={(e) => handleGenreChange(1, e.target.value)}
                      placeholder="Write your genre..."
                      disabled={!canEdit || isSubmitting}
                      className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 w-full"
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="genre-3" className="block text-sm font-medium text-gray-300">
                      Genre 3
                    </label>
                    <Input
                      id="genre-3"
                      type="text"
                      value={genres[2]}
                      onChange={(e) => handleGenreChange(2, e.target.value)}
                      placeholder="Write your genre..."
                      disabled={!canEdit || isSubmitting}
                      className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Setlist & Timestamps Section */}
              <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-2">Setlist & Timestamps <span className="text-white/30 font-normal">(Optional)</span></h3>
                <p className="text-sm text-gray-400 mb-4">Add the tracks from your setlist in the order they were played. Including timestamps is optional.</p>
                <Textarea
                  disabled={!canEdit || isSubmitting} 
                  id="setlist"
                  value={displayedSetlist}
                  onChange={(e) => setDisplayedSetlist(e.target.value)}
                  rows={15}
                  className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 w-full rounded-md p-3 text-sm"
                  placeholder={`00:00:00 - Love Is In The Air\n00:00:01 - Saturday\n00:00:02 - Love Will Save Me\n00:00:03 - Ember\n00:00:04 - Refresh\n00:00:05 - Nefarious\n00:00:06 - Neighborhood (Live Remix)\n00:00:07 - Never Alone\n00:00:08 - Hibernation\n00:00:09 - Heartless\n00:00:10 - Tell Me Are We\n00:00:11 - VUVU\n00:00:12 - Stardust & Secrets\n00:00:13 - Abu Strabel\n00:00:14 - Flying Away With You (ft. Tripolism)\n00:00:15 - Closer\n00:00:16 - Heads Above (Live Acoustic Version)`}
                />
              </div>

              {/* Philanthropic Cause Section (read-only) */}
              <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-2">Philanthropic Cause <span className="text-white/30 font-normal">(Optional)</span></h3>
                <p className="text-sm text-gray-400 mb-4">This reflects the cause you selected during sign-up.</p>

                <div className="space-y-4">
                  {causeLabel ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Cause Name</label>
                        <div className="bg-[#1a1a1a] border border-white/10 text-gray-400 rounded-md p-3 break-all">
                          {causeLabel}
                        </div>
                      </div>
                      {causeLink && (
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Donations Page URL</label>
                          <div className="bg-[#1a1a1a] border border-white/10 text-gray-400 rounded-md p-3 break-all">
                            {causeLink}
                          </div>
                        </div>
                      )}
                      {causeInstagramUrl && (
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Instagram Page</label>
                          <div className="bg-[#1a1a1a] border border-white/10 text-gray-400 rounded-md p-3 break-all">
                            {causeInstagramUrl}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-[#1a1a1a] border border-white/10 text-gray-500 rounded-md p-3">
                      None
                    </div>
                  )}
                </div>
              </div>

              {/* Submit button */}
              <div className="flex justify-end">
              <Button
                onClick={() => {
                  const filledMoments = keyMomentsUi.filter(km => km.label.trim() !== '');
                  let hasErrors = false;
                  const validated = keyMomentsUi.map(km => {
                    if (km.label.trim() === '') return km;
                    const { normalized, error } = parseAndNormalizeTimestamp(km.timestamp);
                    if (error || !normalized) hasErrors = true;
                    let endError: string | undefined;
                    let normalizedEnd = km.timestampEnd;
                    if (km.endMode === 'custom') {
                      if (!km.timestampEnd.trim()) {
                        endError = 'End time is required for custom clips';
                        hasErrors = true;
                      } else {
                        const endResult = parseAndNormalizeTimestamp(km.timestampEnd);
                        normalizedEnd = endResult.normalized;
                        if (endResult.error) {
                          endError = endResult.error;
                          hasErrors = true;
                        } else if (normalized && normalizedEnd) {
                          const startSec = timestampToSeconds(normalized);
                          const endSec = timestampToSeconds(normalizedEnd);
                          if (endSec <= startSec) {
                            endError = 'End time must be after start time';
                            hasErrors = true;
                          } else if (endSec - startSec > 60) {
                            endError = 'Clip cannot exceed 60 seconds';
                            hasErrors = true;
                          }
                        }
                      }
                    }
                    return { ...km, timestamp: normalized, timestampError: error || (!normalized ? 'Timestamp is required' : undefined), timestampEnd: normalizedEnd, timestampEndError: endError };
                  });
                  if (hasErrors) {
                    setKeyMomentsUi(validated);
                    return;
                  }
                  setKeyMomentsUi(validated);
                  if (filledMoments.length < 5) {
                    setShowValidationDialog(true);
                  } else {
                    setShowConfirmDialog(true);
                  }
                }}
                disabled={isSubmitting || !canEdit || !awsVideoUrl}
                className="w-full sm:w-auto bg-white hover:bg-white/90 text-black font-medium px-8"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
              </div>

              {submitMessage.text && (
                <div className={`mt-4 p-3 rounded-xl text-[14px] text-right ${submitMessage.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
                  {submitMessage.text}
                </div>
              )}
            </div>
          </div>

          <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm submission</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure these are your timestamp choices? This is what we will post on our socials.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08] text-white">
                  Go back
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleMarkAsReady}
                  className="bg-white text-black hover:bg-white/90"
                >
                  Yes, submit
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Not enough timestamps</AlertDialogTitle>
                <AlertDialogDescription>
                  You must enter at least 5 timestamps for the carousel post in order to submit the set.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction className="bg-white text-black hover:bg-white/90">
                  OK
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      </main>

      {/* Share modal (admin only) */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Share this set</DialogTitle>
            <DialogDescription>
              Send this link to the artist so they can review the recording, add timestamps, or download their set.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {shareUrl && (
              <div
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                }}
                className="flex items-center gap-2 bg-[#1a1a1a] border border-white/10 rounded-md px-3 py-2.5 cursor-pointer hover:border-white/20 transition-colors overflow-hidden"
              >
                <span className="text-[13px] text-white/60 break-all flex-1">{shareUrl}</span>
                {linkCopied ? (
                  <Check className="h-4 w-4 text-emerald-400 shrink-0" />
                ) : (
                  <Copy className="h-4 w-4 text-white/40 shrink-0" />
                )}
              </div>
            )}

            {linkCopied && (
              <p className="text-[13px] text-emerald-400 text-center">Copied to clipboard</p>
            )}

            <p className="text-[13px] text-white/30">
              Anyone with this link will be able to view the recording.
            </p>

            <div className="space-y-3 pt-1">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={enableVideoDownload}
                  onCheckedChange={(checked) => setEnableVideoDownload(checked === true)}
                  className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:text-black"
                />
                <span className="text-[14px] text-white/60">Enable Video Download</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={enableAudioDownload}
                  onCheckedChange={(checked) => setEnableAudioDownload(checked === true)}
                  className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:text-black"
                />
                <span className="text-[14px] text-white/60">Enable Audio Download</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={enableTimestampDownloads}
                  onCheckedChange={(checked) => setEnableTimestampDownloads(checked === true)}
                  className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:text-black"
                />
                <span className="text-[14px] text-white/60">Enable Timestamp Downloads</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            {!shareUrl ? (
              <Button
                onClick={() => shareLinkMutation.mutate()}
                disabled={shareLinkMutation.isPending}
                className="bg-white hover:bg-white/90 text-black font-medium px-6"
              >
                {shareLinkMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating...</>
                ) : (
                  "Create & Copy Link"
                )}
              </Button>
            ) : permissionsChanged ? (
              <Button
                onClick={() => shareLinkMutation.mutate()}
                disabled={shareLinkMutation.isPending}
                className="bg-white hover:bg-white/90 text-black font-medium px-6"
              >
                {shareLinkMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Updating...</>
                ) : (
                  "Update & Copy Link"
                )}
              </Button>
            ) : (
              <Button
                onClick={() => setShowShareModal(false)}
                className="bg-white hover:bg-white/90 text-black font-medium px-6"
              >
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <footer className="p-6 mt-12 border-t border-gray-700 bg-clubhouse-black">
        <div className="max-w-6xl mx-auto text-center">
            <h4 className="font-semibold text-gray-100 text-lg">Clubhouse.FM</h4>
            <p className="text-xs text-gray-400">A Non-Profit Community Project Centered on Music & Culture</p>
        </div>
      </footer>
    </div>
  );
};

export default VideoReviewPage;